import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

/**
 * RoleGuard - Restricts access based on user role
 * @param {string|string[]} allowedRoles - Single role or array of allowed roles
 * @param {ReactNode} children - Content to render if authorized
 * @param {string} redirectTo - Where to redirect if unauthorized (default: '/dashboard')
 */
const RoleGuard = ({ allowedRoles, children, redirectTo = "/dashboard" }) => {
  const { user, hasAnyRole } = useAuth();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const roles = Array.isArray(allowedRoles) ? allowedRoles : [allowedRoles];

  if (!hasAnyRole(roles)) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};

/**
 * PermissionGuard - Restricts access based on permission
 * @param {string} permission - Permission name
 * @param {ReactNode} children - Content to render if authorized
 */
export const PermissionGuard = ({
  permission,
  children,
  redirectTo = "/dashboard",
}) => {
  const { user, hasPermission } = useAuth();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!hasPermission(permission)) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};

/**
 * Show component only if user has role
 */
export const ShowIfRole = ({ roles, children }) => {
  const { user, hasAnyRole } = useAuth();

  if (!user) return null;
  if (!hasAnyRole(roles)) return null;

  return children;
};

/**
 * Show component only if user has permission
 */
export const ShowIfPermission = ({ permission, children }) => {
  const { user, hasPermission } = useAuth();

  if (!user) return null;
  if (!hasPermission(permission)) return null;

  return children;
};

export default RoleGuard;
